import {useCallback} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

import {type MediaBrowserPanelConfig} from '../common';
import {keysToTitle} from '../utils/getTitleFromConfig';

export type UpdateConfigWithMediaKeysOptions = {
  search: string;
  pathname: string;
  replace: (url: string) => void;
};

/**
 * Pure function that applies config updates and optionally syncs panel display
 * name to the URL when mediaKeys change. Extracted for easy unit testing without
 * mocking React or react-router.
 *
 * Paths:
 * - mediaKeys provided and empty → no-op (updateConfig not called)
 * - mediaKeys not provided → updateConfig(updates) only
 * - mediaKeys provided (non-empty): may update URL if on panel page and
 *   chartTitle is unset and current panelDisplayName differs from keysToTitle(mediaKeys);
 *   then updateConfig({...updates, mediaKeys})
 */
export function applyUpdateConfigWithMediaKeys(
  updates: Partial<MediaBrowserPanelConfig>,
  config: MediaBrowserPanelConfig,
  updateConfig: (config: Partial<MediaBrowserPanelConfig>) => void,
  options: UpdateConfigWithMediaKeysOptions
): void {
  const {search, pathname, replace} = options;
  const mediaKeys = updates.mediaKeys;

  if (mediaKeys != null && mediaKeys.length === 0) {
    return;
  }

  if (mediaKeys) {
    const searchParams = new URLSearchParams(search);
    const panelDisplayName = searchParams.get('panelDisplayName');
    const defaultChartTitle = keysToTitle(mediaKeys);
    if (
      panelDisplayName &&
      config.chartTitle == null &&
      panelDisplayName !== defaultChartTitle
    ) {
      searchParams.set('panelDisplayName', defaultChartTitle);
      replace(`${pathname}?${searchParams.toString()}`);
    }
  }

  updateConfig({
    // reset step and index when media keys change, because
    // the current values may be invalid for the new key(s)
    stepIndex: undefined,
    mediaIndex: undefined,
    // assume any updates to the step/index with media keys
    // are valid for the new key(s)
    ...updates,
    ...(mediaKeys && {mediaKeys}),
  });
}

export function useUpdateConfigWithMediaKeys() {
  const history = useHistory();
  const location = useLocation();

  return useCallback(
    (
      updates: Partial<MediaBrowserPanelConfig>,
      config: MediaBrowserPanelConfig,
      updateConfig: (config: Partial<MediaBrowserPanelConfig>) => void
    ) => {
      applyUpdateConfigWithMediaKeys(updates, config, updateConfig, {
        search: location.search,
        pathname: location.pathname,
        replace: url => history.replace(url),
      });
    },
    [history, location]
  );
}
